import {createContext, PropsWithChildren, useContext, Context} from "react";

import {useSearchParams} from "react-router-dom";

import {
  useActionDetailsManage,
  UseActionDetailsManageProps,
} from "@/hooks/useActionDetailsManage";

import {ActionRecordModel} from "@/api/models";

export type ActionDetailsContextValue<T extends ActionRecordModel> = ReturnType<
  typeof useActionDetailsManage<T>
>;

export const ActionDetailsContext =
  createContext<ActionDetailsContextValue<ActionRecordModel> | null>(null);

export const useActionDetailsContext = <
  T extends ActionRecordModel = ActionRecordModel,
>(
  strict = true,
) => {
  const context = useContext<ActionDetailsContextValue<T> | null>(
    ActionDetailsContext as unknown as Context<ActionDetailsContextValue<T> | null>,
  );

  if (strict && !context) {
    throw new Error(
      "useActionDetailsContext must be used within a ActionDetailsContextProvider",
    );
  }

  return context;
};

export type ActionDetailsContextProviderProps = PropsWithChildren &
  Omit<UseActionDetailsManageProps, "id"> & {
    id?: string;
  };

export const ActionDetailsContextProvider = <
  T extends ActionRecordModel = ActionRecordModel,
>(
  props: ActionDetailsContextProviderProps,
) => {
  const [searchParams] = useSearchParams();
  const {id: propsId, actionType, children} = props;

  const id = propsId || searchParams.get("action_id");

  const manage = useActionDetailsManage<T>({id, actionType});
  const {data} = manage;

  if (id && !data) {
    return null;
  }

  return (
    <ActionDetailsContext.Provider value={{...manage}}>
      {children}
    </ActionDetailsContext.Provider>
  );
};
